type AudioProbe = (url: string, signal: AbortSignal) => Promise<boolean | null>;

/** One soundtrack, matching export priority. All original mute states are restored. */
export class PreviewAudio {
  private readonly controller = new AbortController();
  private readonly originalMuted: boolean[];
  private selected = 0;
  private active = false;
  private enabled = true;
  private detected = false;
  readonly ready: Promise<void>;

  get hasAudio(): boolean { return this.detected; }
  setEnabled(enabled: boolean): void { this.enabled = enabled; if (this.active) this.activate(); }

  constructor(private readonly clips: HTMLVideoElement[], inspect: AudioProbe = inspectAudioTrack) {
    this.originalMuted = clips.map(clip => clip.muted);
    this.ready = this.selectTrack(inspect);
  }

  activate(): void {
    if (this.controller.signal.aborted) return;
    this.active = true;
    this.clips.forEach((clip, index) => { clip.muted = !this.enabled || index !== this.selected; });
  }

  deactivate(): void {
    this.active = false;
    this.clips.forEach((clip, index) => { clip.muted = this.originalMuted[index]; });
  }

  dispose(): void { this.controller.abort(); this.deactivate(); }

  private async selectTrack(inspect: AudioProbe): Promise<void> {
    let unknown = -1;
    let selected = -1;
    for (let index = 0; index < this.clips.length; index++) {
      const clip = this.clips[index];
      let result: boolean | null = null;
      try { result = await inspect(clip.currentSrc || clip.src, this.controller.signal); } catch { /* Native playback can still support an unreadable container. */ }
      if (this.controller.signal.aborted) return;
      if (result === null && unknown < 0) unknown = index;
      if (result) { selected = index; break; }
    }
    this.selected = selected >= 0 ? selected : unknown;
    this.detected = selected >= 0;
    if (this.active) this.activate();
  }
}

async function inspectAudioTrack(url: string, signal: AbortSignal): Promise<boolean | null> {
  if (!url || signal.aborted) return null;
  const { Input, UrlSource, ALL_FORMATS } = await import('mediabunny');
  if (signal.aborted) return null;
  const input = new Input({ formats: ALL_FORMATS, source: new UrlSource(url, { maxCacheSize: 4 * 1024 * 1024, parallelism: 1 }) });
  const dispose = () => input.dispose();
  signal.addEventListener('abort', dispose, { once: true });
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      input.getPrimaryAudioTrack().then(track => Boolean(track)),
      new Promise<null>(resolve => { timer = setTimeout(() => { dispose(); resolve(null); }, 4000); })
    ]);
  } catch { return null; }
  finally { clearTimeout(timer); signal.removeEventListener('abort', dispose); dispose(); }
}
